'use client';

import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { EllipsisVerticalIcon } from 'lucide-react';
import { toast } from 'sonner';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ApiEmployee = ApiSchemas['EmployeeRead'];

export const ADMIN_EMPLOYEE_COLUMNS: ColumnDef<ApiEmployee>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row: { original } }) => {
      return (
        <span>
          {original.first_name} {original.last_name}
        </span>
      );
    },
  },
  {
    header: 'Email',
    accessorKey: 'email',
  },
  {
    header: 'Role',
    accessorKey: 'role',
    cell: ({ row }) => {
      return <span className='capitalize'>{row.original.role.toLowerCase()}</span>;
    },
  },
  {
    header: 'Office',
    accessorKey: 'office_id',
    cell: ({ row }) => <OfficeName employee={row.original} />,
  },
  {
    id: 'actions',
    cell: ({ row }) => <ActionCell employee={row.original} />,
  },
];

function OfficeName({ employee }: { employee: ApiEmployee }) {
  const { api } = useApiV1Ctx();
  const { data } = api.offices.listAllOffices.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const office = useMemo(() => {
    return data?.find((office) => office.id === employee.office_id);
  }, [data, employee.office_id]);
  return <span>{office?.name}</span>;
}

function ActionCell({ employee }: { employee: ApiEmployee }) {
  const { api } = useApiV1Ctx();
  const mutation = api.employees.switchToEmployee.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Employee status updated');
      window.location.reload();
    },
  });

  return (
    <div className='flex justify-end'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='icon'>
            <EllipsisVerticalIcon className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onSelect={() => mutation.mutate({ query: { employee_id: employee.id } })}>
            Switch to User
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
